import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Collapse, Button, Pagination, Alert } from 'react-bootstrap';
import ActionTooltipButton from './ActionTooltipButton';
import Swal from '../helpers/swal';
import { toast } from 'react-toastify';
import campaignService from '../../services/campaign.service';
import Loading from '../common/Loading';
import CampaignListFilters from './CampaignListFilters';
import { useEyeconxStore } from '../../store/eyeconxStore';
import { toProperCase, truncateText } from '../process/processQueueUtils';
const CAMPAIGN_STATUS_OPTIONS = ['PENDING', 'IN_PROGRESS', 'COMPLETED', 'ERROR'];
const STARTABLE_STATUSES = ['PENDING', 'IN_PROGRESS'];

const CAMPAIGN_STATUS_LABELS = {
    PENDING: 'Pending',
    IN_PROGRESS: 'In Progress',
    COMPLETED: 'Completed',
    ERROR: 'Error',
};

const CAMPAIGN_STATUS_BADGES = {
    PENDING: 'text-bg-info',
    IN_PROGRESS: 'text-bg-primary',
    COMPLETED: 'text-bg-success',
    ERROR: 'text-bg-danger',
};

const CAMPAIGN_STATUS_ICONS = {
    PENDING: 'bi-hourglass-split',
    IN_PROGRESS: 'bi-arrow-repeat',
    COMPLETED: 'bi-check-circle-fill',
    ERROR: 'bi-exclamation-circle-fill',
};

function getStatusKey(item) {
    if (item?.status && CAMPAIGN_STATUS_OPTIONS.includes(item.status)) {
        return item.status;
    }
    if (item?.statusCode != null && CAMPAIGN_STATUS_OPTIONS[item.statusCode]) {
        return CAMPAIGN_STATUS_OPTIONS[item.statusCode];
    }
    return null;
}

function CampaignTable({ searchPanelOpen = false }) {
    const navigate = useNavigate();
    const user = useEyeconxStore((state) => state.user);
    const isAdmin = user?.role === 'ADMIN';
    const [itemsPerPage, setItemsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [campaigns, setCampaigns] = useState([]);
    const [expandedRowId, setExpandedRowId] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [startingCampaignId, setStartingCampaignId] = useState(null);

    const [typeFilter, setTypeFilter] = useState('all');
    const [categoryFilter, setCategoryFilter] = useState('all');

    const fetchList = useCallback(() => {
        setIsLoading(true);
        setError(null);
        campaignService
            .getCampaigns(currentPage, itemsPerPage, {
                type: typeFilter,
                category: categoryFilter,
            })
            .then((res) => {
                setCampaigns(res.data.content || []);
                setTotalPages(res.data.totalPages || 0);
                setTotalElements(res.data.totalElements || 0);
                if (
                    (res.data.content || []).length === 0 &&
                    (typeFilter !== 'all' || categoryFilter !== 'all')
                ) {
                    setError('No campaigns match your filters.');
                }
            })
            .catch(() => {
                setError('Failed to load campaigns. Please try again.');
                setCampaigns([]);
            })
            .finally(() => setIsLoading(false));
    }, [currentPage, itemsPerPage, typeFilter, categoryFilter]);

    useEffect(() => {
        fetchList();
    }, [fetchList]);

    const handleTypeFilterChange = (value) => {
        setTypeFilter(value);
        setCurrentPage(0);
    };

    const handleCategoryFilterChange = (value) => {
        setCategoryFilter(value);
        setCurrentPage(0);
    };

    const handleClearFilters = () => {
        setTypeFilter('all');
        setCategoryFilter('all');
        setCurrentPage(0);
    };

    const handleItemsPerPageChange = (event) => {
        setItemsPerPage(parseInt(event.target.value, 10));
        setCurrentPage(0);
    };

    const paginate = (pageNumber) => setCurrentPage(pageNumber);

    const toggleRow = (id) => {
        setExpandedRowId((prev) => (prev === id ? null : id));
    };

    const navigateWithInfo = (path, item) => {
        navigate(path, {
            state: {
                info: {
                    ...item,
                    status: item.statusCode,
                },
            },
        });
    };

    const handleDelete = (item) => {
        Swal.fire({
            title: 'Delete campaign?',
            html: `"<strong>${item.name || 'Untitled'}</strong>" and its queue/results will be permanently deleted.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#d33',
            confirmButtonText: 'Delete',
        }).then((result) => {
            if (!result.isConfirmed) return;

            campaignService
                .deleteCampaign(item.id)
                .then(() => {
                    toast.success('Campaign deleted', { position: toast.POSITION.BOTTOM_RIGHT });
                    if (campaigns.length === 1 && currentPage > 0) {
                        setCurrentPage((page) => page - 1);
                    } else {
                        fetchList();
                    }
                })
                .catch(() => {
                    toast.error('Failed to delete campaign', { position: toast.POSITION.BOTTOM_RIGHT });
                });
        });
    };

    const handleStartSending = (item) => {
        const isSms = item.type === 'SMS';
        const channelLabel = isSms ? 'SMS' : 'email';
        const isNewsletter = String(item.category || '').toUpperCase() === 'NEWSLETTER';
        const preferenceNote = isNewsletter
            ? `<p class="mb-2 text-muted small">Patients opted out of newsletters will be marked <strong>Unsubscribed</strong> and will not receive this ${channelLabel}.</p>`
            : '';

        Swal.fire({
            title: isNewsletter ? 'Start sending this newsletter?' : 'Start sending this campaign?',
            html: `
                <p class="mb-2">Waiting ${channelLabel} queue items will be queued for delivery in the background.</p>
                ${preferenceNote}
                <p class="mb-0 text-muted small">You can leave this page — EyeconX-Processor will pick them up and send them out.</p>
            `,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#198754',
            confirmButtonText: 'Start Sending',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (!result.isConfirmed) return;

            setStartingCampaignId(item.id);
            campaignService
                .startSendingCampaign(item.id)
                .then((res) => {
                    const queuedCount = res?.data?.queuedCount ?? 0;
                    toast.success(
                        `Start sending accepted for ${queuedCount} ${channelLabel} message${
                            queuedCount === 1 ? '' : 's'
                        }. Publishing runs in the background.`,
                        { position: toast.POSITION.BOTTOM_RIGHT }
                    );
                    fetchList();
                })
                .catch((err) => {
                    const message =
                        err?.response?.data?.errorMessage ||
                        err?.response?.data?.message ||
                        'Failed to start sending. Please try again.';
                    toast.error(message, { position: toast.POSITION.BOTTOM_RIGHT });
                })
                .finally(() => setStartingCampaignId(null));
        });
    };

    const getPaginationItems = () => {
        const items = [];
        const maxVisible = 5;

        if (totalPages < 1) return items;

        items.push(
            <Pagination.Item key={1} active={currentPage === 0} onClick={() => paginate(0)}>
                1
            </Pagination.Item>
        );

        let startPage = Math.max(2, currentPage);
        let endPage = Math.min(totalPages - 1, currentPage + 2);

        if (currentPage < 3) endPage = Math.min(totalPages - 1, maxVisible);
        if (currentPage > totalPages - 4) startPage = Math.max(2, totalPages - maxVisible + 1);

        if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" disabled />);

        for (let number = startPage; number <= endPage; number++) {
            items.push(
                <Pagination.Item
                    key={number}
                    active={number === currentPage + 1}
                    onClick={() => paginate(number - 1)}
                >
                    {number}
                </Pagination.Item>
            );
        }

        if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" disabled />);

        if (totalPages > 1) {
            items.push(
                <Pagination.Item
                    key={totalPages}
                    active={totalPages === currentPage + 1}
                    onClick={() => paginate(totalPages - 1)}
                >
                    {totalPages}
                </Pagination.Item>
            );
        }

        return items;
    };

    const paginationControls =
        totalPages > 1 ? (
            <Pagination className="mb-0">
                <Pagination.First onClick={() => paginate(0)} disabled={currentPage === 0} />
                <Pagination.Prev onClick={() => paginate(currentPage - 1)} disabled={currentPage === 0} />
                {getPaginationItems()}
                <Pagination.Next
                    onClick={() => paginate(currentPage + 1)}
                    disabled={currentPage >= totalPages - 1}
                />
                <Pagination.Last
                    onClick={() => paginate(totalPages - 1)}
                    disabled={currentPage >= totalPages - 1}
                />
            </Pagination>
        ) : null;

    const startItem = campaigns.length > 0 ? currentPage * itemsPerPage + 1 : 0;
    const endItem = Math.min((currentPage + 1) * itemsPerPage, totalElements);

    const StatusBadge = ({ status, statusCode }) => {
        const key = status || (statusCode != null ? CAMPAIGN_STATUS_OPTIONS[statusCode] : null);
        const badgeClass = CAMPAIGN_STATUS_BADGES[key] || 'text-bg-secondary';
        const label = CAMPAIGN_STATUS_LABELS[key] || key || 'Unknown';
        const icon = CAMPAIGN_STATUS_ICONS[key] || 'bi-question-circle';
        return (
            <span className={`badge ${badgeClass}`}>
                <i className={`bi ${icon} me-1`}></i>
                {label}
            </span>
        );
    };

    const TypeBadge = ({ type }) =>
        type === 'EMAIL' ? (
            <span className="badge bg-primary bg-opacity-10 text-primary">
                <i className="bi bi-envelope me-1"></i>
                Email
            </span>
        ) : (
            <span className="badge bg-success bg-opacity-10 text-success">
                <i className="bi bi-chat-dots me-1"></i>
                SMS
            </span>
        );

    const DetailField = ({ label, children, mono }) => (
        <div className="col-md-4 col-lg-3 mb-2">
            <div className="text-muted small">{label}</div>
            <div className={`small ${mono ? 'font-monospace text-break' : ''}`}>{children ?? '—'}</div>
        </div>
    );

    return (
        <>
            <div className="d-flex align-items-center flex-wrap gap-1 mb-3">
                <i className="bi bi-table text-primary"></i>
        <h5 className="card-title mb-0" style={{ color: '#012970' }}>
                    Campaign List
                    <span className="text-muted fw-normal" style={{ fontSize: '0.9rem', marginLeft: '0.35rem' }}>
                        ({totalElements} campaign{totalElements !== 1 ? 's' : ''} total)
                    </span>
                </h5>
            </div>

            <Collapse in={searchPanelOpen}>
                <div>
                    <CampaignListFilters
                        typeFilter={typeFilter}
                        onTypeFilterChange={handleTypeFilterChange}
                        categoryFilter={categoryFilter}
                        onCategoryFilterChange={handleCategoryFilterChange}
                        onClear={handleClearFilters}
                        canClear={typeFilter !== 'all' || categoryFilter !== 'all'}
                    />
                </div>
            </Collapse>

            {isLoading ? (
                <Loading />
            ) : error && campaigns.length === 0 ? (
                <Alert variant="warning">{error}</Alert>
            ) : (
                <div className="table-responsive">
                    <table className="table table-bordered table-hover align-middle mb-0 process-queue-table">
                        <thead className="table-light">
                            <tr>
                                <th>Clinic</th>
                                <th>Type</th>
                                <th>Category</th>
                                <th>Name / Title</th>
                                <th>Status</th>
                                <th>Created</th>
                                <th style={{ width: isAdmin ? '220px' : '150px' }}>Actions</th>
                                <th style={{ width: '56px' }}></th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.length === 0 ? (
                                <tr>
                                    <td colSpan={8} className="text-center text-muted py-4">
                                        No campaigns found.
                                    </td>
                                </tr>
                            ) : (
                                campaigns.map((item) => (
                                    <React.Fragment key={item.id}>
                                        <tr className={expandedRowId === item.id ? 'table-active' : ''}>
                                            <td>
                                                <div className="fw-medium small">{item.clinicName || '—'}</div>
                                            </td>
                                            <td>
                                                <TypeBadge type={item.type} />
                                            </td>
                                            <td>
                                                <small>{toProperCase(item.category)}</small>
                                            </td>
                                            <td>
                                                <div className="fw-medium small">{item.name || '—'}</div>
                                                <small className="text-muted">
                                                    {truncateText(item.title, 60) || '—'}
                                                </small>
                                            </td>
                                            <td>
                                                <StatusBadge status={item.status} statusCode={item.statusCode} />
                                            </td>
                                            <td>
                                                <small>{item.createdAt || '—'}</small>
                                            </td>
                                            <td>
                                                <div className="d-flex flex-wrap gap-1">
                                                    {isAdmin && STARTABLE_STATUSES.includes(getStatusKey(item)) && (
                                                        <ActionTooltipButton
                                                            variant="outline-success"
                                                            size="sm"
                                                            tooltip="Start sending"
                                                            tooltipId={`campaign-start-${item.id}`}
                                                            disabled={startingCampaignId === item.id}
                                                            onClick={() => handleStartSending(item)}
                                                        >
                                                            <i
                                                                className={`bi ${
                                                                    startingCampaignId === item.id
                                                                        ? 'bi-hourglass-split'
                                                                        : 'bi-send-fill'
                                                                }`}
                                                            ></i>
                                                        </ActionTooltipButton>
                                                    )}
                                                    <ActionTooltipButton
                                                        variant="outline-primary"
                                                        size="sm"
                                                        tooltip="Preview"
                                                        tooltipId={`campaign-preview-${item.id}`}
                                                        onClick={() => navigateWithInfo(`/campaign/preview/${item.id}`, item)}
                                                    >
                                                        <i className="bi bi-eye"></i>
                                                    </ActionTooltipButton>
                                                    <ActionTooltipButton
                                                        variant="outline-secondary"
                                                        size="sm"
                                                        tooltip="Statistics"
                                                        tooltipId={`campaign-stats-${item.id}`}
                                                        onClick={() => navigateWithInfo(`/campaign/stats/${item.id}`, item)}
                                                    >
                                                        <i className="bi bi-bar-chart"></i>
                                                    </ActionTooltipButton>
                                                    {isAdmin && (
                                                        <ActionTooltipButton
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            tooltip={
                                                                item.type === 'SMS'
                                                                    ? 'SMS queue'
                                                                    : 'Email queue'
                                                            }
                                                            tooltipId={`campaign-queue-${item.id}`}
                                                            onClick={() => navigateWithInfo(`/campaign/queue/${item.id}`, item)}
                                                        >
                                                            <i
                                                                className={`bi ${
                                                                    item.type === 'SMS'
                                                                        ? 'bi-chat-dots'
                                                                        : 'bi-list-ol'
                                                                }`}
                                                            ></i>
                                                        </ActionTooltipButton>
                                                    )}
                                                    <ActionTooltipButton
                                                        variant="outline-danger"
                                                        size="sm"
                                                        tooltip="Stop campaign"
                                                        tooltipId={`campaign-stop-${item.id}`}
                                                        onClick={() => navigateWithInfo(`/campaign/stop/${item.id}`, item)}
                                                    >
                                                        <i className="bi bi-stop-circle"></i>
                                                    </ActionTooltipButton>
                                                    {isAdmin && (
                                                        <ActionTooltipButton
                                                            variant="outline-danger"
                                                            size="sm"
                                                            tooltip="Delete campaign"
                                                            tooltipId={`campaign-delete-${item.id}`}
                                                            onClick={() => handleDelete(item)}
                                                        >
                                                            <i className="bi bi-trash"></i>
                                                        </ActionTooltipButton>
                                                    )}
                                                </div>
                                            </td>
                                            <td className="text-center">
                                                <Button
                                                    variant={
                                                        expandedRowId === item.id
                                                            ? 'outline-secondary'
                                                            : 'outline-primary'
                                                    }
                                                    size="sm"
                                                    onClick={() => toggleRow(item.id)}
                                                    title={expandedRowId === item.id ? 'Collapse' : 'Expand'}
                                                    aria-expanded={expandedRowId === item.id}
                                                >
                                                    <i
                                                        className={`bi bi-chevron-${
                                                            expandedRowId === item.id ? 'up' : 'down'
                                                        }`}
                                                    ></i>
                                                </Button>
                                            </td>
                                        </tr>
                                        <tr className="p-0 border-top-0">
                                            <td colSpan={8} className="p-0 border-top-0">
                                                <Collapse in={expandedRowId === item.id}>
                                                    <div className="p-3 bg-light border-top">
                                                        <div className="row">
                                                            <DetailField label="Campaign ID" mono>
                                                                {item.id}
                                                            </DetailField>
                                                            <DetailField label="Clinic Name">{item.clinicName}</DetailField>
                                                            <DetailField label="Clinic ID" mono>
                                                                {item.clinicId}
                                                            </DetailField>
                                                            <DetailField label="Type">{item.type}</DetailField>
                                                            <DetailField label="Category">{item.category}</DetailField>
                                                            <DetailField label="Status">
                                                                <StatusBadge
                                                                    status={item.status}
                                                                    statusCode={item.statusCode}
                                                                />
                                                            </DetailField>
                                                            <DetailField label="Status Code">{item.statusCode}</DetailField>
                                                            <DetailField label="Created At">{item.createdAt}</DetailField>
                                                            <DetailField label="Modified At">{item.modifiedAt}</DetailField>
                                                            <div className="col-12 mb-2">
                                                                <div className="text-muted small">Description</div>
                                                                <div className="small">
                                                                    {truncateText(item.description, 500) || '—'}
                                                                </div>
                                                            </div>
                                                            {item.contentPath && (
                                                                <div className="col-12 mb-2">
                                                                    <div className="text-muted small">Content</div>
                                                                    <div className="small font-monospace text-break">
                                                                        {truncateText(item.contentPath, 500)}
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                </Collapse>
                                            </td>
                                        </tr>
                                    </React.Fragment>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mt-3">
                <div className="d-flex flex-wrap align-items-center gap-3">
                    <div className="d-flex align-items-center gap-2">
                        <span className="small text-muted">Show</span>
                        <select
                            className="form-select form-select-sm"
                            style={{ width: '80px' }}
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                        >
                            {[10, 20, 50, 100].map((n) => (
                                <option key={n} value={n}>
                                    {n}
                                </option>
                            ))}
                        </select>
                        <span className="small text-muted">entries</span>
                    </div>
                    <span className="text-muted small">
                        Showing {startItem} to {endItem} of {totalElements}
                    </span>
                </div>
                {paginationControls}
            </div>
        </>
    );
}

export default CampaignTable;
